import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Divider,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { toastr } from 'react-redux-toastr';
import useReactHookFormProps from '../../../../hooks/useReactHookFormProps';
import { post } from '../../../../utils/api';
import { sanitizeNumberInput } from '../../../../utils/redux-form-helper';
import { useScreeningPurposes } from '../../../ApplicationProfile/LeaseApplicationTab/hooks.queries';
import messages from '../messages';

const EMPLOYMENT_STATUS_OPTIONS = [
  { value: 'Employed', text: 'Employed' },
  { value: 'Not Employed', text: 'Not Employed' },
  { value: 'Retired', text: 'Retired' },
  { value: 'Other', text: 'Other' },
];

type Props = {
  intl: Object,
  open: boolean,
  onClose: () => void,
  onSuccess?: () => void,
  applicationId: string,
  organizationId: string,
  propertyId: string,
  screeningTypes: Array<{ value: string, text: string }>,
  householdMembers: Array<Object>,
  applicantsValidationResult: ?Object,
};

function ResidentScreeningModalContent({
  intl,
  open,
  onClose,
  onSuccess,
  applicationId,
  organizationId,
  propertyId,
  screeningTypes,
  householdMembers,
  applicantsValidationResult,
}: Props) {
  const { screeningPurposes } = useScreeningPurposes();

  const purposeOptions = useMemo(
    () =>
      screeningPurposes
        .filter((p) => p.name !== 'Application')
        .map((p) => ({ value: p.id, text: p.name }))
        .sort((a, b) => {
          if (a.text === 'Other' && b.text === 'Other') return 0;
          if (a.text === 'Other') return 1;
          if (b.text === 'Other') return -1;
          return a.text.localeCompare(b.text);
        }),
    [screeningPurposes],
  );

  // Members with only income/employment data missing — still selectable, will show dynamic fields
  const membersWithOnlyIncomeOrEmploymentMissing = useMemo(
    () =>
      new Set(
        applicantsValidationResult?.parsedErrors
          ?.applicantCustomerIdsWithOnlyMissingIncomeOrEmployment ?? [],
      ),
    [applicantsValidationResult],
  );

  // Members who have income/employment missing (may also have other fields missing)
  const membersWithMissingIncomeOrEmployment = useMemo(
    () =>
      new Set(
        applicantsValidationResult?.parsedErrors
          ?.applicantCustomerIdsWithMissingIncomeOrEmployment ?? [],
      ),
    [applicantsValidationResult],
  );

  // Members with missing/invalid fields who are NOT exclusively missing income/employment — remain disabled
  const membersWithOtherFieldsMissing = useMemo(() => {
    const allMissingIds = new Set(
      applicantsValidationResult?.parsedErrors
        ?.applicantCustomerIdsWithMissingOrInvalidFields ?? [],
    );
    const result = new Set();
    allMissingIds.forEach((id) => {
      if (!membersWithOnlyIncomeOrEmploymentMissing.has(id)) {
        result.add(id);
      }
    });
    return result;
  }, [applicantsValidationResult, membersWithOnlyIncomeOrEmploymentMissing]);

  const incompleteTooltipText = useMemo(
    () => intl.formatMessage(messages.incompleteApplicationFieldsTooltip),
    [intl],
  );

  const missingMiniProfileTooltipText = useMemo(
    () => intl.formatMessage(messages.missingMiniProfileFieldsTooltip),
    [intl],
  );

  const memberById = useMemo(() => {
    const map = {};
    (householdMembers ?? []).forEach((m) => {
      const id = m.customerId ?? m.id;
      const name = (
        m.name ?? `${m.firstName ?? ''} ${m.lastName ?? ''}`
      ).trim();
      map[id] = { ...m, name };
    });
    return map;
  }, [householdMembers]);

  // Build a map of customerId -> existing employmentStatus from the screening API data
  const existingEmploymentStatusByCustomerId = useMemo(() => {
    const map = {};
    (applicantsValidationResult?.screeningApplicants ?? []).forEach((a) => {
      if (a.customerId && a.employmentStatus) {
        map[a.customerId] = a.employmentStatus;
      }
    });
    return map;
  }, [applicantsValidationResult]);

  // Build a map of customerId -> existing annualIncome from the screening API data
  const existingAnnualIncomeByCustomerId = useMemo(() => {
    const map = {};
    (applicantsValidationResult?.screeningApplicants ?? []).forEach((a) => {
      const parsed = parseFloat(a.annualIncome);
      if (a.customerId && Number.isFinite(parsed) && parsed > 0) {
        map[a.customerId] = parsed;
      }
    });
    return map;
  }, [applicantsValidationResult]);

  // Set of customerIds who are current residents (have a ResidentCustomer record)
  const residentCustomerIds = useMemo(
    () =>
      new Set(
        (applicantsValidationResult?.screeningApplicants ?? [])
          .filter((a) => a.isResident)
          .map((a) => a.customerId)
          .filter(Boolean),
      ),
    [applicantsValidationResult],
  );

  // Residents who are Employed in the DB but have no income — Joi passes (0 >= 0) so they
  // won't appear in parsedErrors; we detect them directly from the raw applicant data
  const employedResidentsWithNoIncome = useMemo(
    () =>
      new Set(
        (applicantsValidationResult?.screeningApplicants ?? [])
          .filter(
            (a) =>
              a.isResident &&
              a.employmentStatus === 'Employed' &&
              !existingAnnualIncomeByCustomerId[a.customerId],
          )
          .map((a) => a.customerId)
          .filter(Boolean),
      ),
    [applicantsValidationResult, existingAnnualIncomeByCustomerId],
  );

  const nameByValue = useMemo(() => {
    const map = {};
    Object.entries(memberById).forEach(([id, m]) => {
      map[id] = m.name;
    });
    return map;
  }, [memberById]);

  const householdMemberOptions = useMemo(
    () =>
      (householdMembers ?? [])
        .map((m) => {
          const id = m.customerId ?? m.id;
          const name = (
            m.name ?? `${m.firstName ?? ''} ${m.lastName ?? ''}`
          ).trim();
          const isExistingMember = residentCustomerIds.has(id);
          const isMissingOtherFields = membersWithOtherFieldsMissing.has(id);

          let disabledProps = {};
          if (isMissingOtherFields) {
            if (isExistingMember) {
              disabledProps = {
                disabled: true,
                tooltip: missingMiniProfileTooltipText,
              };
            } else {
              disabledProps = {
                disabled: true,
                tooltip: incompleteTooltipText,
              };
            }
          }

          return {
            value: id,
            text: name,
            _name: name,
            ...disabledProps,
          };
        })
        .sort((a, b) => a._name.localeCompare(b._name)),
    [
      householdMembers,
      membersWithOtherFieldsMissing,
      residentCustomerIds,
      incompleteTooltipText,
      missingMiniProfileTooltipText,
    ],
  );

  const renderHouseholdMemberValue = useCallback(
    (selected) => {
      if (!Array.isArray(selected) || selected.length === 0) {
        return intl.formatMessage(messages.selectHouseholdMember);
      }
      return selected
        .map((v) => nameByValue[v])
        .filter(Boolean)
        .join(', ');
    },
    [intl, nameByValue],
  );

  const reactHookFormProps = useReactHookFormProps({
    mode: 'all',
    defaultValues: {
      estimatedMonthlyRentAmount: '',
      purposeOfScreening: '',
      screeningType: [],
      householdMember: [],
    },
  });

  const { reset, watch, setValue } = reactHookFormProps;

  useEffect(() => {
    reset();
  }, [open, reset]);

  const rentValue = watch('estimatedMonthlyRentAmount');
  useEffect(() => {
    if (rentValue === undefined || rentValue === '') return;
    const sanitized = sanitizeNumberInput(String(rentValue));
    if (sanitized !== String(rentValue)) {
      setValue('estimatedMonthlyRentAmount', sanitized, {
        shouldValidate: true,
      });
    }
  }, [rentValue, setValue]);

  const selectedMemberIds = watch('householdMember');

  // Current members who are selected, are current residents, and need income/employment data
  const selectedMembersNeedingData = useMemo(
    () =>
      (selectedMemberIds ?? [])
        .filter((id) => {
          if (!residentCustomerIds.has(id)) return false;
          return (
            membersWithMissingIncomeOrEmployment.has(id) ||
            employedResidentsWithNoIncome.has(id)
          );
        })
        .map((id) => memberById[id])
        .filter(Boolean),
    [
      selectedMemberIds,
      membersWithMissingIncomeOrEmployment,
      employedResidentsWithNoIncome,
      residentCustomerIds,
      memberById,
    ],
  );

  // Watch each selected member's employment status field directly
  const employmentStatusFields = selectedMembersNeedingData.map(
    (m) => `memberData.${m.customerId ?? m.id}.employmentStatus`,
  );
  const employmentStatusValues = watch(employmentStatusFields);

  // Watch annual income fields so we can keep the field visible if a value exists
  const annualIncomeFields = selectedMembersNeedingData.map(
    (m) => `memberData.${m.customerId ?? m.id}.annualIncome`,
  );
  const annualIncomeValues = watch(annualIncomeFields);

  // Pre-populate employment status and annual income from existing DB data when a member gets selected
  useEffect(() => {
    selectedMembersNeedingData.forEach((m, index) => {
      const memberId = m.customerId ?? m.id;

      const existingStatus = existingEmploymentStatusByCustomerId[memberId];
      const currentStatusValue =
        employmentStatusValues?.[index] ?? employmentStatusValues;
      if (existingStatus && !currentStatusValue) {
        setValue(`memberData.${memberId}.employmentStatus`, existingStatus, {
          shouldValidate: true,
        });
      }

      const existingIncome = existingAnnualIncomeByCustomerId[memberId];
      const currentIncomeValue =
        annualIncomeValues?.[index] ?? annualIncomeValues;
      if (existingIncome && !currentIncomeValue) {
        setValue(`memberData.${memberId}.annualIncome`, existingIncome, {
          shouldValidate: true,
        });
      }
    });
  }, [
    selectedMembersNeedingData,
    existingEmploymentStatusByCustomerId,
    existingAnnualIncomeByCustomerId,
    employmentStatusValues,
    annualIncomeValues,
    setValue,
  ]);

  // Clear annual income when a member's employment status changes away from 'Employed'
  useEffect(() => {
    selectedMembersNeedingData.forEach((m, index) => {
      const memberId = m.customerId ?? m.id;
      const currentStatus =
        employmentStatusValues?.[index] ?? employmentStatusValues;
      const currentIncome = annualIncomeValues?.[index] ?? annualIncomeValues;
      if (
        currentStatus &&
        currentStatus !== 'Employed' &&
        currentIncome != null &&
        currentIncome !== ''
      ) {
        setValue(`memberData.${memberId}.annualIncome`, '', {
          shouldValidate: false,
        });
      }
    });
  }, [
    selectedMembersNeedingData,
    employmentStatusValues,
    annualIncomeValues,
    setValue,
  ]);

  const handleSubmit = async (formValues) => {
    const applicantScreeningData = selectedMembersNeedingData
      .map((m) => {
        const memberId = m.customerId ?? m.id;
        const memberData = formValues.memberData?.[memberId] ?? {};
        const entry = { customerId: memberId };
        if (memberData.employmentStatus) {
          entry.employmentStatus = memberData.employmentStatus;
        }
        if (
          memberData.employmentStatus === 'Employed' &&
          memberData.annualIncome !== undefined &&
          memberData.annualIncome !== ''
        ) {
          const sanitizedAnnualIncome = sanitizeNumberInput(
            String(memberData.annualIncome),
          );
          const parsedAnnualIncome = Number(sanitizedAnnualIncome);
          if (Number.isFinite(parsedAnnualIncome)) {
            entry.annualIncome = parsedAnnualIncome;
          }
        }
        return entry;
      })
      .filter((e) => e.employmentStatus || e.annualIncome != null);

    try {
      await post(
        `/${organizationId}/${propertyId}/screening-service/resident-submit`,
        JSON.stringify({
          applicationId,
          monthlyRentAmount: formValues.estimatedMonthlyRentAmount,
          screeningPurposeId: formValues.purposeOfScreening,
          requestedScreeningMethodIds: formValues.screeningType,
          requestedApplicantIds: formValues.householdMember,
          ...(applicantScreeningData.length > 0 && { applicantScreeningData }),
        }),
      );
      toastr.success(
        intl.formatMessage(messages.screeningSubmittedTitle),
        intl.formatMessage(messages.screeningSubmittedMessage),
      );
      onClose();
      if (onSuccess) onSuccess();
    } catch (e) {
      toastr.error(
        intl.formatMessage(messages.screeningErrorTitle),
        intl.formatMessage(messages.screeningErrorMessage),
      );
    }
  };

  // Build dynamic per-member missing data fields
  const memberDataFields = useMemo(() => {
    if (selectedMembersNeedingData.length === 0) return [];

    const dynamicFields = [];

    selectedMembersNeedingData.forEach((m, index) => {
      const memberId = m.customerId ?? m.id;
      const memberName = m.name;
      const employmentStatusFieldName = `memberData.${memberId}.employmentStatus`;
      const annualIncomeFieldName = `memberData.${memberId}.annualIncome`;

      // Get current employment status from the watched array (index matches selectedMembersNeedingData order)
      const currentEmploymentStatus =
        employmentStatusValues?.[index] ?? employmentStatusValues;
      const isEmployed = currentEmploymentStatus === 'Employed';
      // Also show income for residents who are Employed in DB but have no income yet
      const employedResidentNeedsIncome =
        employedResidentsWithNoIncome.has(memberId) && !currentEmploymentStatus;
      const showAnnualIncome = isEmployed || employedResidentNeedsIncome;

      if (index === 0) {
        // One section header before the first member
        dynamicFields.push({
          variant: 'custom',
          name: 'memberData_section_header',
          component: (
            <Stack spacing={1} sx={{ width: '100%' }}>
              <Divider />
              <Typography variant="labelLarge">
                {intl.formatMessage(messages.missingInfoHeader)}
              </Typography>
            </Stack>
          ),
          GridProps: { xs: 12 },
        });
      } else {
        // Divider between members
        dynamicFields.push({
          variant: 'custom',
          name: `memberData_divider_${memberId}`,
          component: <Divider />,
          GridProps: { xs: 12 },
        });
      }

      // Member name label
      dynamicFields.push({
        variant: 'custom',
        name: `memberData_name_${memberId}`,
        component: (
          <Typography variant="titleSmall" color="text.secondary">
            {memberName}
          </Typography>
        ),
        GridProps: { xs: 12 },
      });

      dynamicFields.push({
        variant: 'singleSelect',
        name: employmentStatusFieldName,
        label: intl.formatMessage(messages.employmentStatus),
        placeholder: intl.formatMessage(messages.selectEmploymentStatus),
        options: EMPLOYMENT_STATUS_OPTIONS,
        rules: { required: true },
        GridProps: { xs: 6 },
      });

      if (showAnnualIncome) {
        dynamicFields.push({
          variant: 'currency',
          name: annualIncomeFieldName,
          label: intl.formatMessage(messages.annualIncome),
          placeholder: '0.00',
          rules: {
            required: true,
            validate: (v) => {
              const num = Number(sanitizeNumberInput(String(v)));
              if (!Number.isFinite(num))
                return intl.formatMessage(messages.annualIncomeError);
              if (isEmployed && num <= 0)
                return intl.formatMessage(messages.annualIncomeError);
              if (num > 999999999999.99)
                return intl.formatMessage(messages.annualIncomeMaxError);
              return true;
            },
          },
          GridProps: { xs: 6 },
        });
      }
    });

    return dynamicFields;
  }, [
    selectedMembersNeedingData,
    employmentStatusValues,
    employedResidentsWithNoIncome,
    intl,
  ]);

  const fields = useMemo(
    () => [
      {
        variant: 'currency',
        name: 'estimatedMonthlyRentAmount',
        label: intl.formatMessage(messages.estimatedMonthlyRentAmount),
        InputLabelProps: {
          TooltipProps: {
            children: <AlertInfoIcon />,
            title: intl.formatMessage(
              messages.estimatedMonthlyRentAmountTooltip,
            ),
          },
        },
        placeholder: '0.00',
        rules: {
          required: true,
          validate: (v) =>
            (Number(v) >= 1 && Number(v) <= 25000) ||
            intl.formatMessage(messages.estimatedMonthlyRentAmountError),
        },
        GridProps: { xs: 6 },
      },
      {
        variant: 'singleSelect',
        name: 'purposeOfScreening',
        label: intl.formatMessage(messages.purposeOfScreening),
        placeholder: intl.formatMessage(messages.selectPurposeOfScreening),
        options: purposeOptions,
        rules: { required: true },
        GridProps: { xs: 6 },
      },
      {
        variant: 'multiSelect',
        name: 'screeningType',
        label: intl.formatMessage(messages.screeningType),
        placeholder: intl.formatMessage(messages.selectScreeningType),
        options: screeningTypes,
        rules: { required: true },
        GridProps: { xs: 6 },
      },
      {
        variant: 'multiSelect',
        name: 'householdMember',
        label: intl.formatMessage(messages.householdMember),
        InputLabelProps: {
          TooltipProps: {
            children: <AlertInfoIcon />,
            title: intl.formatMessage(messages.householdMemberTooltip),
          },
        },
        placeholder: intl.formatMessage(messages.selectHouseholdMember),
        options: householdMemberOptions,
        renderValue: renderHouseholdMemberValue,
        rules: { required: true },
        GridProps: { xs: 6 },
      },
      ...memberDataFields,
    ],
    [
      intl,
      purposeOptions,
      screeningTypes,
      householdMemberOptions,
      renderHouseholdMemberValue,
      memberDataFields,
    ],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={intl.formatMessage(messages.modalTitle)}
      actionsProps={[
        {
          variant: 'text',
          children: intl.formatMessage(messages.cancel),
          onClick: onClose,
        },
        {
          children: intl.formatMessage(messages.confirmAndSubmit),
          submitButton: true,
          disabled:
            reactHookFormProps.isSubmitting ||
            !reactHookFormProps.isValid ||
            !reactHookFormProps.isDirty,
          isSubmitting: reactHookFormProps.isSubmitting,
          onClick: reactHookFormProps.handleSubmit(handleSubmit),
        },
      ]}
      ModalProps={{
        PaperProps: {
          sx: {
            width: {
              xs: 'calc(100% - 32px)',
              sm: 600,
            },
          },
        },
      }}
    >
      <Form
        ReactHookFormProps={reactHookFormProps}
        fields={fields}
        spacing={2}
        GridContainerProps={{ alignItems: 'flex-start' }}
      />
    </Modal>
  );
}

function ResidentScreeningModal(props: Props) {
  const { open } = props;
  if (!open) {
    return null;
  }
  return <ResidentScreeningModalContent {...props} />;
}

export default ResidentScreeningModal;
